import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Search, Filter as FilterIcon, Upload } from 'lucide-react';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize, Pagination } from '../../../shared/pagination';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import SavedFilterTabs from '../../../shared/SavedFilterTabs';
import FilterDrawer from '../../../shared/FilterDrawer';
import CancelButton from '../../../shared/CancelButton';
import '../../../shared/ListRowHover.css';
import { mediaFileUrl as fileUrl, thumbFileUrl } from '../../../shared/mediaFileUrl';
import '../../../shared/StyledInput.css';
import { askConfirm } from '../../../shared/AppDialogs';

const CATEGORY_LABELS = { technical: 'Technical', marketing: 'Marketing', branding: 'Branding', entity_logo: 'Entity Logo', brand_logo: 'Brand Logo', attachment: 'Attachment' };

// Same SavedFilterTabs pattern as Field Help Settings/Products — a quick-
// select dropdown plus the drawer for saved tabs, both bound to the same
// filters object (screen has one filterable field, Category).
const FILTER_SCREEN_KEY = 'media-library';
const DEFAULT_FILTERS = { category: '' };
const FIELD_FILTER_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_FILTER_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };


function MediaThumb({ item }) {
  if (item.mime_type === 'application/pdf') {
    return (
      <a
        href={fileUrl(item.id)}
        target="_blank"
        rel="noreferrer"
        style={{ width: 42, height: 42, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#eef1f5', borderRadius: 4, color: '#5a6b85', fontSize: 11, fontWeight: 700, textDecoration: 'none' }}
      >
        PDF
      </a>
    );
  }
  return (
    <img
      src={thumbFileUrl(item.id)}
      alt=""
      width={42}
      height={42}
      style={{ display: 'block', objectFit: 'contain', borderRadius: 4, border: '1px solid #cdd5df', background: '#f6f8fa' }}
    />
  );
}

// Row-level rename — a local draft, only sent to the server on Save; Cancel
// discards it with nothing written, same shape as ErrorCatalogEditor's
// inline draft (ErrorCatalogPage.js). Renaming the shared library row
// itself, not a per-Product attachment, so the new name is what every
// attaching Product sees.
function MediaRow({ item, onRename, onDelete }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.original_filename);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const startEdit = () => {
    setDraft(item.original_filename);
    setEditing(true);
  };

  const save = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return showError(null, 'Filename cannot be empty.');
    if (trimmed === item.original_filename) return setEditing(false);
    setSaving(true);
    try {
      await onRename(item.id, trimmed);
      setEditing(false);
      dismissError();
    } catch (err) {
      showError(err, 'Could not rename image');
    }
    setSaving(false);
  };

  // Deletion itself is only actually blocked server-side (RESTRICT) while
  // still attached to a Product or set as the company logo — rather than
  // duplicating that "is it in use" check here, the button is always
  // offered and a blocked attempt surfaces the backend's own clear message.
  const handleDelete = async () => {
    if (!(await askConfirm(`Delete "${item.original_filename}" from the Media Library? This cannot be undone.`, { confirmLabel: 'Delete', destructive: true }))) return;
    setDeleting(true);
    try {
      await onDelete(item.id);
      dismissError();
    } catch (err) {
      showError(err, 'Could not delete image');
      setDeleting(false);
    }
  };

  return (
    <tr
      className="list-row"
      onClick={() => { if (!editing) startEdit(); }}
      style={{ cursor: editing ? 'default' : 'pointer' }}
      title={editing ? undefined : 'Click to edit'}
    >
      <td style={{ padding: '6px 8px' }} onClick={(e) => e.stopPropagation()}><MediaThumb item={item} /></td>
      <td style={{ padding: '6px 8px', fontSize: 14 }}>
        {editing ? (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoFocus
              style={{ flex: 1, minWidth: 160, padding: '5px 8px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }}
            />
            <button
              type="button"
              onClick={save}
              disabled={saving}
              style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: saving ? '#90caf9' : '#1976d2', color: '#fff', fontWeight: 600, fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer' }}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={saving}
              style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: '#ff6f61', color: '#fff', fontWeight: 600, fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer' }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <span title={item.original_filename}>{item.original_filename}</span>
        )}
      </td>
      <td style={{ padding: '6px 8px', fontSize: 14 }}>{CATEGORY_LABELS[item.category] || item.category}</td>
      <td style={{ padding: '6px 8px', fontSize: 14, color: item.in_use ? '#333' : '#aaa' }}>{item.in_use ? 'Yes' : 'No'}</td>
      <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
        <RowActionsMenu
          ariaLabel="Media actions"
          items={[
            { key: 'rename', label: 'Rename', onClick: startEdit, disabled: deleting },
            { key: 'delete', label: deleting ? 'Deleting...' : 'Delete', onClick: handleDelete, disabled: deleting, danger: true },
          ]}
        />
      </td>
    </tr>
  );
}

// Media Library — the shared library's own list, independent of any one
// Product (vis_product_media_library.md). Every image lives here whether or
// not it's currently attached to a Product; renaming here is what every
// attaching Product sees (the SP400/SP400BK shared-drawing case). Attaching/
// detaching stays on each Product's own Product Media page — this page only
// knows about the library, not any one Product's relationship to it.
function MediaLibraryPage() {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const { category } = filters;
  const applyFilters = (values) => { setFilters(values); setPage(1); };
  // Ad hoc "Filters" panel — draft is null while closed, so opening always
  // starts from the currently-applied filters rather than whatever was left
  // over from the last time it was open.
  const [filterDraft, setFilterDraft] = useState(null);
  const activeFilterCount = Object.keys(DEFAULT_FILTERS).filter((k) => String(filters[k] || '') !== String(DEFAULT_FILTERS[k] || '')).length;
  const openFilterPanel = () => setFilterDraft({ ...filters });
  const closeFilterPanel = () => setFilterDraft(null);
  const setFilterDraftField = (key, value) => setFilterDraft((prev) => ({ ...prev, [key]: value }));
  const applyFilterPanel = () => { applyFilters(filterDraft); closeFilterPanel(); };
  const clearFilterPanel = () => { applyFilters(DEFAULT_FILTERS); closeFilterPanel(); };
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);

  useDefaultPageSize(setLimit);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/media-images', { params: { search, category, page, limit } });
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      setRows([]);
      setPagination({ page, limit, total: 0, pages: 1 });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [search, category, page, limit]);

  const handleRename = async (id, filename) => {
    const res = await axios.patch(`/api/media-images/${id}`, { original_filename: filename });
    setRows((prev) => prev.map((r) => (r.id === id ? res.data.data : r)));
  };

  const handleDelete = async (id) => {
    await axios.delete(`/api/media-images/${id}`);
    setRows((prev) => prev.filter((r) => r.id !== id));
    setPagination((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
  };

  // Attachment Library upload (story #237) — a bare 'attachment' category
  // MediaImage, never attached to a Product or any other record. Plain
  // file input (same shape as CompanySettings.js's LogoField), not a
  // modal — the file itself is the only input needed. Re-fetches the
  // current page afterward rather than splicing the new row in locally,
  // since the new file needs to land in its alphabetical sort position.
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const handleUploadClick = () => fileInputRef.current?.click();
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post('/api/media-images/attachments', formData);
      dismissError();
      await fetchRows();
    } catch (err) {
      showError(err, 'Could not upload attachment');
    }
    setUploading(false);
  };

  // The drawer's field list for SavedFilterTabs — the same Category field
  // that used to also live in an always-visible quick select above it,
  // duplicating this same field twice on screen.
  const renderDrawerFields = (draft, setDraftField) => (
    <div>
      <label style={FIELD_FILTER_LABEL_STYLE}>Category</label><br />
      <select className="styled-input" value={draft.category} onChange={(e) => setDraftField('category', e.target.value)} style={FIELD_FILTER_INPUT_STYLE}>
        <option value="">All categories</option>
        <option value="marketing">Marketing</option>
        <option value="technical">Technical</option>
        <option value="branding">Branding</option>
        <option value="entity_logo">Entity Logo</option>
        <option value="brand_logo">Brand Logo</option>
        <option value="attachment">Attachment</option>
      </select>
    </div>
  );

  return (
    <div style={{ margin: '32px 24px 48px', background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', paddingBottom: 32 }}>
      <h2 style={{ padding: '20px 24px 4px', margin: 0, fontWeight: 600, fontSize: 20 }}>Media Library</h2>
      <p style={{ padding: '0 24px', margin: '0 0 12px', color: '#888', fontSize: 12 }}>
        Every image and drawing in the shared library, whether or not it's currently attached to a Product. Renaming here changes what every attaching Product sees.
      </p>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', margin: '0 24px 14px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#fff', border: '1px solid #d0d5dd', borderRadius: 8, maxWidth: 280 }}>
          <Search size={14} strokeWidth={1.75} color="#888" />
          <input
            type="text"
            placeholder="Search filenames..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ border: 'none', outline: 'none', fontSize: 14, width: '100%' }}
          />
        </div>
        <button
          type="button"
          onClick={openFilterPanel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #bfc8d6',
            background: activeFilterCount ? '#eef1f5' : '#fff',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <FilterIcon size={15} strokeWidth={2} />
          Filters
          {activeFilterCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 700 }}>
              {activeFilterCount}
            </span>
          )}
        </button>
        <input ref={fileInputRef} type="file" accept="application/pdf" onChange={handleFileChange} style={{ display: 'none' }} />
        <button
          type="button"
          onClick={handleUploadClick}
          disabled={uploading}
          title="Upload a PDF to the Attachment Library"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #bfc8d6',
            background: '#fff',
            fontSize: 14,
            fontWeight: 500,
            cursor: uploading ? 'not-allowed' : 'pointer',
            marginLeft: 'auto',
          }}
        >
          <Upload size={15} strokeWidth={2} />
          {uploading ? 'Uploading...' : 'Upload Attachment'}
        </button>
      </div>

      <div style={{ margin: '0 24px 14px' }}>
        <SavedFilterTabs
          screenKey={FILTER_SCREEN_KEY}
          defaultFilters={DEFAULT_FILTERS}
          currentFilters={filters}
          onApply={applyFilters}
          renderFields={renderDrawerFields}
        />
      </div>

      {filterDraft && (
        <FilterDrawer
          open
          onOpenChange={(next) => { if (!next) closeFilterPanel(); }}
          title="Filters"
          description="Narrow the media library list."
          footer={(
            <>
              <div>
                <button type="button" onClick={clearFilterPanel} style={{ padding: '8px 12px', border: '1px solid #bfc8d6', color: '#5a6b85', background: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Clear filters
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <CancelButton onClick={closeFilterPanel} />
                <button type="button" onClick={applyFilterPanel} style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Apply
                </button>
              </div>
            </>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {renderDrawerFields(filterDraft, setFilterDraftField)}
          </div>
        </FilterDrawer>
      )}

      <div style={{ margin: '0 24px', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '6%' }} />
            <col style={{ width: '52%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Media</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Filename</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Category</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>In Use</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No images match.</td></tr>
            ) : rows.map((item) => (
              <MediaRow key={item.id} item={item} onRename={handleRename} onDelete={handleDelete} />
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        rightContent={(
          <select className="styled-input" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }}>
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default MediaLibraryPage;
